"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { getStoredLocale, setStoredLocale, normalizeLocaleForContext } from "@/utils/localeCookie";

type Locale = "en" | "fr";

type LocaleContextType = {
    currentLocale: Locale;
    switchLanguage: (locale: Locale) => void;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Get current locale from cookie
const getCurrentLocale = (): Locale => {
    if (typeof window === "undefined") return "en";

    // Priority 1: Check hostname for CADA French domain
    const hostname = window.location.hostname;
    if (hostname.includes("cadaetudedelamaindoeuvre")) {
        return "fr";
    }

    // Priority 2: Check NEXT_LOCALE cookie
    const storedLocale = getStoredLocale();
    if (storedLocale) {
        return normalizeLocaleForContext(storedLocale);
    }

    return "en";
};

export function LocaleProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    const [currentLocale, setCurrentLocale] = useState<Locale>("en");

    useEffect(() => {
        const locale = getCurrentLocale();
        setCurrentLocale(locale);

        if (typeof window !== "undefined") {
            const hostname = window.location.hostname;
            // For the CADA French domain, always set French locale
            if (hostname.includes("cadaetudedelamaindoeuvre") && locale === "fr") {
                setStoredLocale("fr-CA");
            } else {
                // Set cookie if not already set
                const storedLocale = getStoredLocale();
                if (!storedLocale) {
                    // Map "fr" to "fr-CA" for consistency with i18next
                    const cookieLocale = locale === "fr" ? "fr-CA" : locale;
                    setStoredLocale(cookieLocale);
                }
            }
        }

        // Listen for locale changes from other components (e.g., LocaleSelector)
        const handleLocaleChange = (e: CustomEvent<{ locale: string }>) => {
            const newLocale = normalizeLocaleForContext(e.detail.locale);
            setCurrentLocale(newLocale);
        };

        window.addEventListener("localeChange", handleLocaleChange as EventListener);
        return () => {
            window.removeEventListener("localeChange", handleLocaleChange as EventListener);
        };
    }, []);

    const switchLanguage = (locale: Locale) => {
        // Map "fr" to "fr-CA" for consistency with i18next
        const cookieLocale = locale === "fr" ? "fr-CA" : locale;

        setStoredLocale(cookieLocale); // This dispatches the localeChange event
        setCurrentLocale(locale);

        // Refresh to apply new locale
        router.refresh();
    };

    return (
        <LocaleContext.Provider
            value={{
                currentLocale,
                switchLanguage,
            }}
        >
            {children}
        </LocaleContext.Provider>
    );
}

export function useLocale() {
    const context = useContext(LocaleContext);
    if (context === undefined) {
        throw new Error("useLocale must be used within a LocaleProvider");
    }
    return context;
}
